import Phaser from "phaser";
import { BaseWindow } from "../../../ui/windows/base-window";

const HEADER_HEIGHT = 46;
const PANEL_WIDTH = 220;
const TAB_W = 42;
const TAB_H = 68;

const C_SHADOW = 0x3d2000;
const C_BORDER = 0x5c3010;
const C_ACCENT = 0xb88a3c;
const C_BG     = 0xcfb07a;
const C_HEADER = 0x9e6a28;
const C_RULE   = 0x5c3010;
const C_TITLE  = "#f5e8c0";
const C_STROKE = "#3d1800";

const LS_SEEN_KEY = "seen-news";

export class NewsWindow extends BaseWindow {
  private isOpen: boolean = false;
  private latestNewsDate: string = "";
  private peekTween: Phaser.Tweens.Tween | null = null;
  private onSceneClick: (() => void) | null = null;

  constructor(scene: Phaser.Scene) {
    super(scene);
  }

  show(): void {
    this.hide();
    if (!this.scene) return;

    const { width: screenWidth, height: screenHeight } = this.scene.scale;
    this.width = PANEL_WIDTH;
    this.height = screenHeight;

    const { width, height } = this;
    const elements: Phaser.GameObjects.GameObject[] = [];

    // Left shadow strip for depth
    const shadow = this.scene.add.graphics();
    shadow.fillStyle(C_SHADOW, 0.45);
    shadow.fillRect(-10, 0, 10, height);
    shadow.setScrollFactor(0);
    elements.push(shadow);

    // Main background (parchment)
    this.bg = this.scene.add.graphics();
    this.bg.fillStyle(C_BG, 0.98);
    this.bg.fillRect(0, 0, width, height);
    this.bg.setScrollFactor(0);
    elements.push(this.bg);

    // Left border
    const border = this.scene.add.graphics();
    border.lineStyle(3, C_BORDER, 1);
    border.lineBetween(0, 0, 0, height);
    border.setScrollFactor(0);
    elements.push(border);

    // Inner left accent
    const accent = this.scene.add.graphics();
    accent.lineStyle(1, C_ACCENT, 0.5);
    accent.lineBetween(4, 0, 4, height);
    accent.setScrollFactor(0);
    elements.push(accent);

    // Header background
    const headerBg = this.scene.add.graphics();
    headerBg.fillStyle(C_HEADER, 1);
    headerBg.fillRect(0, 0, width, HEADER_HEIGHT);
    headerBg.setScrollFactor(0);
    elements.push(headerBg);

    // Header rule
    const rule = this.scene.add.graphics();
    rule.lineStyle(2, C_RULE, 0.8);
    rule.lineBetween(2, HEADER_HEIGHT, width - 2, HEADER_HEIGHT);
    rule.setScrollFactor(0);
    elements.push(rule);

    const pad = this.CONTENT_PADDING_X;

    this.titleText = this.scene.add
      .text(pad, HEADER_HEIGHT / 2 - 1, "NEWS", {
        fontSize: "22px",
        color: C_TITLE,
        fontFamily: "GothicCondensed",
        align: "left",
        stroke: C_STROKE,
        strokeThickness: 3,
      })
      .setOrigin(0, 0.5)
      .setScrollFactor(0);
    elements.push(this.titleText);

    const wrapWidth = width - pad * 2;
    const startY = HEADER_HEIGHT + 16;

    const newsTitle = this.scene.add.text(pad, startY, "", {
      fontSize: "22px",
      color: "#4a2e00",
      fontFamily: "Anton",
      align: "left",
      wordWrap: { width: wrapWidth },
      lineSpacing: 2,
    }).setOrigin(0, 0).setScrollFactor(0);
    elements.push(newsTitle);

    const newsContent = this.scene.add.text(pad, startY, "", {
      fontSize: "22px",
      color: "#4a2e00",
      fontFamily: "GothicCondensed",
      align: "left",
      wordWrap: { width: wrapWidth },
      lineSpacing: 3,
    }).setOrigin(0, 0).setScrollFactor(0);
    elements.push(newsContent);

    const newsDate = this.scene.add.text(
      width - pad,
      HEADER_HEIGHT / 2 - 1,
      "",
      {
        fontSize: "18px",
        color: C_TITLE,
        fontFamily: "GothicCondensed",
        align: "right",
      }
    ).setOrigin(1, 0.5).setScrollFactor(0);
    elements.push(newsDate);

    // Invisible hit zone blocks click-through to elements behind the panel
    const hitZone = this.scene.add.zone(width / 2, height / 2, width, height).setInteractive();
    elements.push(hitZone);

    // Tab button — sits to the left of the panel, slides with it
    const tabY = Math.floor((height - TAB_H) / 2);
    const tabG = this.scene.add.graphics();
    // Shadow
    tabG.fillStyle(C_SHADOW, 0.4);
    tabG.fillRoundedRect(-TAB_W - 4, tabY + 3, TAB_W + 1, TAB_H, { tl: 10, bl: 10, tr: 0, br: 0 });
    // Parchment fill
    tabG.fillStyle(C_BG, 1);
    tabG.fillRoundedRect(-TAB_W, tabY, TAB_W, TAB_H, { tl: 10, bl: 10, tr: 0, br: 0 });
    // Border
    tabG.lineStyle(2, C_BORDER, 1);
    tabG.strokeRoundedRect(-TAB_W, tabY, TAB_W, TAB_H, { tl: 10, bl: 10, tr: 0, br: 0 });
    // Inner accent
    tabG.lineStyle(1, C_ACCENT, 0.6);
    tabG.strokeRoundedRect(-TAB_W + 2, tabY + 2, TAB_W - 4, TAB_H - 4, { tl: 8, bl: 8, tr: 0, br: 0 });
    tabG.setScrollFactor(0);
    elements.push(tabG);

    const tabLabel = this.scene.add.text(-TAB_W / 2, tabY + TAB_H / 2, "i", {
      fontSize: "26px",
      color: "#4a2e00",
      fontFamily: "Georgia, 'Times New Roman', serif",
      fontStyle: "italic bold",
    }).setOrigin(0.5, 0.5).setScrollFactor(0);
    elements.push(tabLabel);

    const tabZone = this.scene.add
      .zone(-TAB_W / 2, tabY + TAB_H / 2, TAB_W, TAB_H)
      .setInteractive();
    tabZone.on("pointerdown", () => this.toggle());
    tabZone.on("pointerover", () => this.scene.input.setDefaultCursor("pointer"));
    tabZone.on("pointerout", () => this.scene.input.setDefaultCursor("default"));
    elements.push(tabZone);

    fetch("/data/news.json")
      .then((r) => r.json())
      .then(([item]: [{ title: string; content: string; date: string }]) => {
        newsTitle.setText(item.title);
        newsContent.setY(startY + newsTitle.height + 20);
        newsContent.setText(item.content);
        newsDate.setText(item.date);
        this.latestNewsDate = item.date;
        if (localStorage.getItem(LS_SEEN_KEY) !== item.date) {
          this.startPeekAnimation();
        }
      })
      .catch(() => { newsTitle.setText("No updates\navailable"); });

    this.container = this.scene.add.container(screenWidth, 0, elements);
    this.container.setDepth(200000);
    this.visible = true;
    this.isOpen = false;
    this.addContainerToParent(this.container);
  }

  private startPeekAnimation(): void {
    if (!this.container) return;
    const { width: screenWidth } = this.scene.scale;
    const bounces: [number, number, number][] = [
      [62, 220, 180],
      [36, 165, 130],
      [17, 120,  95],
      [ 6,  80,  65],
    ];
    const step = (i: number) => {
      if (i >= bounces.length || !this.container) return;
      const [peek, outDur, inDur] = bounces[i];
      this.peekTween = this.scene.tweens.add({
        targets: this.container,
        x: screenWidth - peek,
        duration: outDur,
        ease: "Sine.Out",
        onComplete: () => {
          if (!this.container) return;
          this.peekTween = this.scene.tweens.add({
            targets: this.container,
            x: screenWidth,
            duration: inDur,
            ease: "Sine.In",
            onComplete: () => step(i + 1),
          });
        },
      });
    };
    step(0);
  }

  slideIn(): void {
    if (!this.container) return;
    const { width: screenWidth } = this.scene.scale;
    this.isOpen = true;
    if (this.peekTween) {
      this.peekTween.stop();
      this.peekTween = null;
    }
    if (this.latestNewsDate) {
      localStorage.setItem(LS_SEEN_KEY, this.latestNewsDate);
    }
    this.scene.tweens.killTweensOf(this.container);
    this.scene.tweens.add({
      targets: this.container,
      x: screenWidth - PANEL_WIDTH,
      duration: 280,
      ease: "Cubic.Out",
    });
    this.scene.time.delayedCall(0, () => {
      if (!this.isOpen) return;
      this.onSceneClick = () => this.slideOut();
      this.scene.input.once("pointerdown", this.onSceneClick);
    });
  }

  slideOut(): void {
    if (!this.container) return;
    const { width: screenWidth } = this.scene.scale;
    this.isOpen = false;
    if (this.onSceneClick) {
      this.scene.input.off("pointerdown", this.onSceneClick);
      this.onSceneClick = null;
    }
    this.scene.tweens.killTweensOf(this.container);
    this.scene.tweens.add({
      targets: this.container,
      x: screenWidth,
      duration: 240,
      ease: "Cubic.In",
    });
  }

  toggle(): void {
    if (this.isOpen) {
      this.slideOut();
    } else {
      this.slideIn();
    }
  }
}
